/* eslint-disable @typescript-eslint/no-explicit-any */
import React, { useEffect, useRef } from "react";
import { useController, useFormContext } from "react-hook-form";
import { COUNTRY_CODE_OPTIONS, DEFAULT_COUNTRY_CODE, splitPhoneNumber } from "util/phoneCountry";

interface PhoneNumberInputProps {
    name: string;
    countryCodeName: string;
    label: string;
    placeholder?: string;
    required?: boolean;
    disabled?: boolean;
    compact?: boolean;
}

const ALLOWED_KEYS = ["Backspace", "Delete", "ArrowLeft", "ArrowRight", "Tab", "Home", "End"];

const PhoneNumberInput = ({
    name,
    countryCodeName,
    label,
    placeholder = "Enter Mobile Number",
    required = false,
    disabled = false,
    compact = false,
}: PhoneNumberInputProps) => {
    const { control, trigger } = useFormContext();

    const {
        field: numberField,
        fieldState: { invalid, error },
    } = useController({ name, control, defaultValue: "" });

    const { field: countryField } = useController({
        name: countryCodeName,
        control,
        defaultValue: DEFAULT_COUNTRY_CODE,
    });

    const selectedCountryCode = countryField.value;
    const enteredNumber = numberField.value;
    const previousCountryCodeRef = useRef(selectedCountryCode);

    useEffect(() => {
        if (previousCountryCodeRef.current === selectedCountryCode) return;

        previousCountryCodeRef.current = selectedCountryCode;
        if (enteredNumber) void trigger(name);
    }, [selectedCountryCode, enteredNumber, name, trigger]);

    const shellClass = `flex w-full items-center rounded-lg border bg-gray-50 text-sm transition-colors
    focus-within:ring-1 focus-within:ring-blue-500 focus-within:border-blue-500
    dark:bg-gray-700 dark:border-gray-600
    ${disabled ? "cursor-not-allowed bg-gray-100 dark:bg-gray-800" : ""}
    ${invalid
            ? "bg-red-50 border-red-500 dark:border-red-500"
            : "border-gray-300"
        }`;

    const handleKeyDown = (event: React.KeyboardEvent<HTMLInputElement>) => {
        if (event.ctrlKey || event.metaKey) return;
        if (!/^\d$/.test(event.key) && !ALLOWED_KEYS.includes(event.key)) {
            event.preventDefault();
        }
    };

    const handlePaste = (event: React.ClipboardEvent<HTMLInputElement>) => {
        if (!/^\d+$/.test(event.clipboardData.getData("text").trim())) {
            event.preventDefault();
        }
    };

    return (
        <div className={compact ? "mb-0" : "mb-2"}>
            <label
                htmlFor={name}
                className={`block min-w-0 text-sm font-medium text-gray-900 dark:text-white leading-snug break-words ${compact ? "mb-1" : "mb-2"
                    }`}
            >
                {label} {required && <span className="text-red-500">*</span>}
            </label>

            <div className={shellClass}>
                <select
                    // style={customSelectStyles}
                    {...countryField}
                    id={countryCodeName}
                    value={countryField.value || DEFAULT_COUNTRY_CODE}
                    disabled={disabled}
                    aria-label={`${label} country calling code`}
                    // CHANGED: Added dark:[color-scheme:dark] at the end of the className string
                    className="h-[42px] w-[88px] shrink-0 appearance-none rounded-l-lg !border-none bg-transparent dark:bg-gray-700 disabled:dark:bg-gray-800 !py-0 !pl-2.5 !pr-7 text-[13px] font-medium text-gray-900 !outline-none !ring-0 dark:text-white disabled:cursor-not-allowed dark:border-gray-600 dark:focus:ring-blue-500 dark:focus:border-blue-500 dark:[color-scheme:dark]"
                >
                    {COUNTRY_CODE_OPTIONS.map((option) => (
                        <option
                            key={option.value}
                            value={option.value}
                            className="bg-white text-gray-900 dark:bg-gray-700 dark:text-white"
                        >
                            {option.label}
                        </option>
                    ))}
                </select>

                <span className="h-5 w-px shrink-0 bg-gray-300 dark:bg-gray-600" />

                <input
                    {...numberField}
                    id={name}
                    type="text"
                    inputMode="numeric"
                    autoComplete="tel-national"
                    placeholder={placeholder}
                    disabled={disabled}
                    value={numberField.value ?? ""}
                    aria-invalid={invalid || undefined}
                    aria-describedby={error?.message ? `${name}-error` : undefined}
                    onKeyDown={handleKeyDown}
                    onPaste={handlePaste}
                    onWheel={(event) => (event.target as HTMLInputElement).blur()}
                    className="h-[42px] min-w-0 flex-1 rounded-r-lg border-none bg-transparent px-2.5 text-sm text-gray-900 placeholder-gray-500 outline-none focus:ring-0 dark:text-white dark:placeholder-gray-400 disabled:cursor-not-allowed"
                />
            </div>

            <div className={compact ? "min-h-[1rem] mt-0.5" : "min-h-[1.5rem] mt-1"}>
                {error?.message ? (
                    <p id={`${name}-error`} className="text-red-500 text-xs font-medium leading-tight">
                        {error.message as string}
                    </p>
                ) : (
                    <p className="invisible text-xs font-medium leading-tight">placeholder</p>
                )}
            </div>
        </div>
    );
};

export const renderPhoneNumber = (storedValue?: string | number | null): React.ReactNode => {
    const { countryCode, nationalNumber } = splitPhoneNumber(storedValue);
    if (!nationalNumber) return "";

    return (
        <span className="whitespace-nowrap">
            {countryCode} <span className="ml-1">{nationalNumber}</span>
        </span>
    );
};

export default PhoneNumberInput;
